import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-transparent border-b border-black/25 focus:border-lima-blue outline-none text-lima-ink text-sm px-0 py-3 rounded-none placeholder:text-black/30 transition-colors duration-200";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-lima-cream text-lima-ink font-body flex flex-col" data-testid="admin-login-page">
      <div className="px-5 md:px-10 h-16 flex items-center border-b border-black/10">
        <Link to="/" data-testid="admin-login-logo">
          <img src="/lima-logo.png" alt="LIMA — Life Inside Media Academy" className="h-9 w-auto" />
        </Link>
      </div>
      <div className="flex-1 grid place-items-center px-5 py-16">
        <motion.div
          initial={{ opacity: 0, y: 24, filter: "blur(12px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-sm"
        >
          <p className="font-mono2 text-[11px] tracking-[0.25em] text-lima-blue mb-4">[ RESTRICTED — CREW ONLY ]</p>
          <h1 className="font-display font-bold text-4xl tracking-tight mb-10">
            Crew <span className="font-serifit font-normal text-lima-blue">sign in.</span>
          </h1>
          <form onSubmit={submit} className="space-y-7" data-testid="admin-login-form">
            <div>
              <label className="block font-mono2 text-[11px] tracking-[0.25em] text-black/50 mb-1">EMAIL</label>
              <input type="email" required data-testid="admin-email-input" className={inputCls} placeholder="admin@lima.academy" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <label className="block font-mono2 text-[11px] tracking-[0.25em] text-black/50 mb-1">PASSWORD</label>
              <input type="password" required data-testid="admin-password-input" className={inputCls} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            {error && (
              <p className="font-mono2 text-xs text-lima-red border-l-2 border-lima-red pl-3 py-1" data-testid="admin-login-error">
                {error}
              </p>
            )}
            <motion.button
              whileTap={{ scale: 0.96 }}
              type="submit"
              disabled={loading}
              data-testid="admin-login-button"
              className="w-full bg-lima-blue hover:bg-lima-bluehover disabled:opacity-50 text-white font-mono2 text-xs font-bold tracking-[0.2em] py-4 transition-colors duration-200 flex items-center justify-center gap-3"
            >
              {loading ? "SIGNING IN…" : "SIGN IN"} <ArrowUpRight size={15} />
            </motion.button>
          </form>
          <Link to="/" data-testid="admin-back-to-site" className="mt-8 inline-flex items-center gap-2 font-mono2 text-xs tracking-[0.2em] text-black/50 hover:text-black">
            <ArrowLeft size={13} /> BACK TO SITE
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
